/** localStorage key: return path when claim editor was opened from messaging. */
export const CLAIM_EDITOR_RETURN_TO_MESSAGING_KEY = 'enter.claimEditor.returnToMessaging';

/** Query flag on claim editor URL so Cancel knows to use the stored messaging return path. */
export const CLAIM_EDITOR_FROM_MESSAGING_PARAM = 'fromMessaging';

export interface IClaimEditorReturnToMessaging {
    /** Absolute app path (pathname + search) that opens the messaging page for this message. */
    path: string;
    messageId?: string;
}

/** Append fromMessaging=1 so the editor Cancel handler can return to messaging. */
export const withClaimEditorFromMessagingQuery = (editPath: string): string => {
    const [pathAndQuery, hash = ''] = editPath.split('#');
    const [pathname, search = ''] = pathAndQuery.split('?');
    const params = new URLSearchParams(search);
    params.set(CLAIM_EDITOR_FROM_MESSAGING_PARAM, '1');
    const query = params.toString();
    return `${pathname}?${query}${hash ? `#${hash}` : ''}`;
};

export const isClaimEditorOpenedFromMessaging = (search: string): boolean => {
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    return params.get(CLAIM_EDITOR_FROM_MESSAGING_PARAM) === '1';
};

export const setClaimEditorReturnToMessaging = (path: string, messageId?: string): void => {
    if (!path) {
        return;
    }
    try {
        const payload: IClaimEditorReturnToMessaging = {
            path,
            ...(messageId ? { messageId } : {}),
        };
        window.localStorage.setItem(CLAIM_EDITOR_RETURN_TO_MESSAGING_KEY, JSON.stringify(payload));
    } catch (error) {
        console.warn('Failed to save claim-editor messaging return path:', error);
    }
};

/** Snapshot current messaging URL before opening claim editor. */
export const rememberMessagingReturnForClaimEditor = (messageId?: string): void => {
    if (typeof window === 'undefined') {
        return;
    }
    const path = `${window.location.pathname}${window.location.search}`;
    setClaimEditorReturnToMessaging(path, messageId);
};

export const peekClaimEditorReturnToMessaging = (): IClaimEditorReturnToMessaging | null => {
    try {
        const raw = window.localStorage.getItem(CLAIM_EDITOR_RETURN_TO_MESSAGING_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as IClaimEditorReturnToMessaging;
        if (!parsed?.path || typeof parsed.path !== 'string') {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
};

export const clearClaimEditorReturnToMessaging = (): void => {
    try {
        window.localStorage.removeItem(CLAIM_EDITOR_RETURN_TO_MESSAGING_KEY);
    } catch (error) {
        console.warn('Failed to clear claim-editor messaging return path:', error);
    }
};

/** Read and clear the return path (for Cancel). */
export const consumeClaimEditorReturnToMessaging = (): IClaimEditorReturnToMessaging | null => {
    const value = peekClaimEditorReturnToMessaging();
    clearClaimEditorReturnToMessaging();
    return value;
};
